import { register } from "./registry";
import { positionedDesigner } from "./template-positioned";

/**
 * Print template designer (Backoffice ▸ Print Templates ▸ Edit, docs/plans/print-templates.md §4 "Flow designer").
 * Edits the same JSON as the Advanced tab: every change rewrites textarea[data-tpl-source] (the saved format) and fires
 * its input event, which redraws the preview; typing in the Advanced tab updates the designer.
 *
 *   <div data-tpl-designer data-tpl-catalog="{fields, lists, required, blockTypes, pageSizes}">
 *
 * Keys in the block list: ↑/↓ Home/End select · Enter edit settings · Ctrl+Ins add · Ctrl+Del remove ·
 * Alt+↑/↓ move · Ctrl+Z undo · Ctrl+Y (or Ctrl+Shift+Z) redo. In the settings: Enter next field, Shift+Enter back,
 * Esc back to the list, Alt+↑/↓ move a field or column.
 */

interface Field { name: string; label: string; kind: string; sample: string }
interface List { name: string; label: string; fields: Field[] }
interface Catalog { fields: Field[]; lists: List[]; required: string[]; blockTypes: string[]; pageSizes: string[] }

interface Item { field?: string; label?: string; format?: string; align?: string; show_if?: string; width?: number; bold?: boolean }
interface Cell { width?: number; blocks: Block[] }
interface Block {
  type: string; text?: string; field?: string; label?: string; rows?: string; show_if?: string; align?: string; size?: number;
  bold?: boolean; italic?: boolean; height_mm?: number; columns?: number; items?: Item[]; cells?: Cell[]; labels?: string[];
}
interface Page { size?: string; width_mm?: number; height_mm?: number; margin_mm?: number; font_size?: number; copy_labels?: string[] }
interface Doc { schema?: number; page?: Page; blocks?: Block[] }

/** "page", a block (path = block index, then cell index and block index for each row it sits in) or a row's column. */
type TreeNode = { kind: "page" } | { kind: "block"; path: number[] } | { kind: "cell"; path: number[] };

const TYPES: Record<string, [string, string]> = {
  company: ["Company heading", "Company name, address, phone and PAN"],
  title: ["Title", "Big heading, e.g. TAX INVOICE"],
  text: ["Text", "Your own words; can include fields"],
  fields: ["Fields", "Label: value pairs"],
  row: ["Columns", "Side-by-side columns, each with its own blocks"],
  table: ["Table", "A table of item lines"],
  list: ["List", "Lines like charges: name … amount"],
  amounts: ["Totals", "Right-aligned label … amount lines"],
  badge: ["Badge", "One field in big bold letters (e.g. TO PAY)"],
  copy_label: ["Copy label", "Original / Duplicate and the copy name"],
  logo: ["Logo", "The company logo"],
  line: ["Line", "A horizontal line"],
  spacer: ["Space", "Empty space"],
  signatures: ["Signatures", "Signature lines with names under them"],
};
const PAGE_SIZES: Record<string, string> = {
  a4: "A4 portrait", a4_landscape: "A4 landscape", a5: "A5 portrait", a5_landscape: "A5 landscape",
  continuous: "Continuous 241 × 279 mm", thermal_80: "Thermal roll 80 mm", custom: "Custom size",
};
const ALIGNS: [string, string][] = [["", "Default"], ["left", "Left"], ["center", "Centre"], ["right", "Right"]];
const FOCUSABLE = "input, select, textarea, button";
const ctrl = (e: KeyboardEvent) => e.ctrlKey || e.metaKey;
const isPositioned = (text: string) => { try { return (JSON.parse(text) as Doc & { mode?: string })?.mode === "positioned"; } catch { return false; } };

register((root) => {
  const host = root.querySelector<HTMLElement>("[data-tpl-designer]");
  const source = document.querySelector<HTMLTextAreaElement>("textarea[data-tpl-source]");
  if (!host || !source || host.dataset.tplBound === "1") return;
  const catalog = JSON.parse(host.dataset.tplCatalog ?? "null") as Catalog | null;
  if (!catalog) return;
  host.dataset.tplBound = "1";
  if (isPositioned(source.value)) { positionedDesigner(host, source, catalog); return; } // pre-printed book (phase 3)
  const fieldLabel = (name?: string) => catalog.fields.find((f) => f.name === name)?.label ?? name ?? "";
  const listOf = (name?: string) => catalog.lists.find((l) => l.name === name);

  // ---- state and history ------------------------------------------------------------------------------------------
  let doc: Doc = {};
  let error: string | null = null;
  const history: string[] = [source.value];
  let at = 0;
  let lastKey: string | null = null;
  let selected = "page";
  let picking = false;
  let writing = false;
  let focusNext: string | null = null;

  const parse = (text: string): boolean => {
    try {
      const d = JSON.parse(text) as Doc;
      if (!d || typeof d !== "object" || Array.isArray(d)) throw new Error("it isn't a template");
      doc = d;
      doc.blocks ??= [];
      error = null;
      return true;
    } catch (e) {
      error = (e as Error).message;
      return false;
    }
  };

  /** Records the change; edits to the same setting in a row (typing) make one undo step. */
  const commit = (key: string | null, structural: boolean) => {
    const text = JSON.stringify(doc, null, 2);
    if (key !== null && key === lastKey && at === history.length - 1) history[at] = text;
    else { history.splice(at + 1); history.push(text); at = history.length - 1; }
    lastKey = key;
    writeSource(text);
    renderTree();
    if (structural) renderPanel(true);
    renderToolbar();
  };

  /** The designer's own writes must not loop back through the Advanced listener below. */
  const writeSource = (text: string) => {
    writing = true;
    try {
      source.value = text;
      source.dispatchEvent(new Event("input", { bubbles: true }));
    } finally { writing = false; }
  };

  const travel = (step: number) => {
    if (at + step < 0 || at + step >= history.length) return;
    at += step;
    lastKey = null;
    parse(history[at] ?? "{}");
    writeSource(history[at] ?? "{}");
    if (!nodes().some((n) => keyOf(n.node) === selected)) selected = "page";
    picking = false;
    renderAll(true);
  };

  source.addEventListener("input", () => {
    if (writing) return;
    const ok = parse(source.value);
    if (ok) {
      if (lastKey === "advanced" && at === history.length - 1) history[at] = source.value;
      else { history.splice(at + 1); history.push(source.value); at = history.length - 1; }
      lastKey = "advanced";
      if (!nodes().some((n) => keyOf(n.node) === selected)) selected = "page";
    }
    renderAll(false);
  });

  // ---- tree helpers -----------------------------------------------------------------------------------------------
  const keyOf = (n: TreeNode) => n.kind === "page" ? "page" : `${n.kind}:${n.path.join(".")}`;
  const nodeOf = (key: string): TreeNode => {
    if (key === "page") return { kind: "page" };
    const [kind, p] = key.split(":");
    return { kind: kind as "block" | "cell", path: (p ?? "").split(".").map(Number) };
  };
  const blockAt = (path: number[]): Block | undefined => {
    let list = doc.blocks ?? [];
    let b: Block | undefined;
    for (let i = 0; i < path.length; i += 2) {
      b = list[path[i]!];
      if (!b) return undefined;
      if (i + 1 < path.length) list = b.cells?.[path[i + 1]!]?.blocks ?? [];
    }
    return b;
  };
  const cellAt = (path: number[]) => blockAt(path.slice(0, -1))?.cells?.[path[path.length - 1]!];
  const siblingsOf = (path: number[]): Block[] => path.length === 1 ? (doc.blocks ??= []) : (cellAt(path.slice(0, -1))?.blocks ?? []);

  const nodes = (): { node: TreeNode; depth: number }[] => {
    const out: { node: TreeNode; depth: number }[] = [{ node: { kind: "page" }, depth: 0 }];
    const walk = (blocks: Block[], prefix: number[], depth: number) => blocks.forEach((b, i) => {
      const path = [...prefix, i];
      out.push({ node: { kind: "block", path }, depth });
      (b.cells ?? []).forEach((c, ci) => {
        out.push({ node: { kind: "cell", path: [...path, ci] }, depth: depth + 1 });
        walk(c.blocks ?? [], [...path, ci], depth + 2);
      });
    });
    walk(doc.blocks ?? [], [], 0);
    return out;
  };

  const summary = (b: Block): string => {
    const items = (b.items ?? []).map((i) => i.label ?? (b.rows ? listOf(b.rows)?.fields.find((f) => f.name === i.field)?.label ?? i.field : fieldLabel(i.field)));
    const text = (() => {
      switch (b.type) {
        case "title": case "text": return b.text ?? "";
        case "fields": case "amounts": return items.join(", ") || "no fields yet";
        case "table": return `${listOf(b.rows)?.label ?? b.rows ?? ""}: ${items.join(", ")}`;
        case "list": return listOf(b.rows)?.label ?? b.rows ?? "";
        case "badge": return fieldLabel(b.field) || "choose a field";
        case "signatures": return (b.labels ?? []).join(", ");
        case "spacer": return `${b.height_mm ?? 3} mm`;
        case "logo": return `${b.height_mm ?? 12} mm high`;
        case "row": return `${b.cells?.length ?? 0} columns`;
        default: return "";
      }
    })();
    return b.show_if ? `${text} · only if ${fieldLabel(b.show_if)}` : text;
  };

  // ---- publish check (same rule as TemplateValidator.CheckPublishable) ---------------------------------------------
  const missing = (): string[] => {
    const shown = new Set<string>();
    let copyLabel = false;
    const placeholders = (t?: string) => [...(t ?? "").matchAll(/\{\{\s*([a-z0-9_.]+)\s*\}\}/g)].forEach((m) => shown.add(m[1]!));
    const walk = (blocks: Block[]) => blocks.filter((b) => !b.show_if).forEach((b) => {
      if (b.type === "copy_label") copyLabel = true;
      if (b.field) shown.add(b.field);
      placeholders(b.text);
      (b.items ?? []).filter((i) => !i.show_if).forEach((i) => { if (i.field) shown.add(b.rows ? `${b.rows}.${i.field}` : i.field); placeholders(i.label); });
      (b.cells ?? []).forEach((c) => walk(c.blocks ?? []));
    });
    walk(doc.blocks ?? []);
    const out = catalog.required.filter((r) => !shown.has(r)).map(fieldLabel);
    if (!copyLabel) out.push("Copy label");
    return out;
  };

  // ---- operations -------------------------------------------------------------------------------------------------
  const newBlock = (type: string): Block => {
    const firstList = catalog.lists[0];
    switch (type) {
      case "title": return { type, text: "TITLE" };
      case "text": return { type, text: "Text" };
      case "fields": case "amounts": return { type, items: [] };
      case "row": return { type, cells: [{ blocks: [] }, { blocks: [] }] };
      case "table": return { type, rows: firstList?.name ?? "", items: (firstList?.fields ?? []).map((f) => ({ field: f.name })) };
      case "list": return { type, rows: (catalog.lists.find((l) => l.name === "charges") ?? catalog.lists[1] ?? firstList)?.name ?? "" };
      case "signatures": return { type, labels: ["Received by", "For the company"] };
      default: return { type };
    }
  };

  const add = (type: string) => {
    const n = nodeOf(selected);
    let target: Block[];
    let index: number;
    let prefix: number[];
    if (n.kind === "cell") { target = cellAt(n.path)!.blocks ??= []; index = target.length; prefix = n.path; }
    else if (n.kind === "block") { target = siblingsOf(n.path); index = n.path[n.path.length - 1]! + 1; prefix = n.path.slice(0, -1); }
    else { target = doc.blocks ??= []; index = 0; prefix = []; }
    target.splice(index, 0, newBlock(type));
    selected = keyOf({ kind: "block", path: [...prefix, index] });
    picking = false;
    commit(null, true);
    focusPanel();
  };

  const remove = () => {
    const n = nodeOf(selected);
    if (n.kind === "page") return;
    const idx = n.path[n.path.length - 1]!;
    if (n.kind === "cell") {
      const row = blockAt(n.path.slice(0, -1))!;
      if ((row.cells?.length ?? 0) <= 1) { say("A row needs at least one column; remove the row instead."); return; }
      row.cells!.splice(idx, 1);
      selected = keyOf({ kind: "cell", path: [...n.path.slice(0, -1), Math.min(idx, row.cells!.length - 1)] });
    } else {
      const sib = siblingsOf(n.path);
      sib.splice(idx, 1);
      const prefix = n.path.slice(0, -1);
      selected = sib.length > 0 ? keyOf({ kind: "block", path: [...prefix, Math.min(idx, sib.length - 1)] })
        : prefix.length > 0 ? keyOf({ kind: "cell", path: prefix }) : "page";
    }
    commit(null, true);
  };

  const move = (step: number) => {
    const n = nodeOf(selected);
    if (n.kind === "page") return;
    const idx = n.path[n.path.length - 1]!;
    const arr: unknown[] = n.kind === "cell" ? blockAt(n.path.slice(0, -1))!.cells! : siblingsOf(n.path);
    const to = idx + step;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    selected = keyOf({ ...n, path: [...n.path.slice(0, -1), to] } as TreeNode);
    commit(null, true);
  };

  // ---- DOM --------------------------------------------------------------------------------------------------------
  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & Record<string, unknown> = {}, ...kids: (globalThis.Node | string)[]) => {
    const e = document.createElement(tag);
    for (const [k, v] of Object.entries(props)) {
      if (k.startsWith("data-") || k.startsWith("aria-") || k === "role") e.setAttribute(k, String(v));
      else (e as unknown as Record<string, unknown>)[k] = v;
    }
    e.append(...kids);
    return e;
  };

  const status = el("p", { className: "tpl-dz-status", role: "status" });
  const say = (msg: string) => { status.textContent = msg; };
  const toolbar = el("div", { className: "tpl-dz-toolbar" });
  const tree = el("ul", { className: "tpl-dz-tree", role: "tree", tabIndex: 0, "aria-label": "Blocks, top to bottom" });
  const panel = el("div", { className: "tpl-dz-panel", role: "region", "aria-label": "Settings" });
  const banner = el("p", { className: "cb-alert cb-alert-warning", role: "alert", hidden: true });
  const need = el("p", { className: "tpl-card-meta tpl-dz-need" });
  host.append(toolbar, banner, el("div", { className: "tpl-dz-body" }, tree, panel), need, status);

  const button = (label: string, title: string, act: () => void, disabled = false) => {
    const b = el("button", { type: "button", className: "cb-btn cb-btn-secondary", textContent: label, title, disabled });
    b.addEventListener("click", act);
    return b;
  };
  const renderToolbar = () => {
    const n = nodeOf(selected);
    toolbar.replaceChildren(
      button("Add block", "Ctrl+Ins: add a block after the selected one (or inside the selected column)", () => openPicker(), !!error),
      button("Remove", "Ctrl+Del", remove, !!error || n.kind === "page"),
      button("Move up", "Alt+↑", () => move(-1), !!error || n.kind === "page"),
      button("Move down", "Alt+↓", () => move(1), !!error || n.kind === "page"),
      button("Undo", "Ctrl+Z", () => travel(-1), at === 0),
      button("Redo", "Ctrl+Y", () => travel(1), at >= history.length - 1));
    const left = error ? [] : missing();
    need.textContent = left.length ? `Still needed before publishing: ${left.join(", ")}.` : "Ready to publish: every required field and the copy label are on the page.";
  };

  const renderTree = () => {
    banner.hidden = !error;
    banner.textContent = error ? `The Advanced text has a mistake, so the designer can't show it: ${error}. Fix it in the Advanced tab.` : "";
    tree.hidden = !!error;
    if (error) return;
    tree.replaceChildren(...nodes().map(({ node, depth }) => {
      const key = keyOf(node);
      const id = `tpl-node-${key.replace(/[^a-z0-9]/gi, "-")}`;
      let name: string;
      let detail: string;
      if (node.kind === "page") { name = "Page"; detail = `${PAGE_SIZES[doc.page?.size ?? "a5_landscape"] ?? doc.page?.size}, ${doc.page?.margin_mm ?? 6} mm margins`; }
      else if (node.kind === "cell") { name = `Column ${node.path[node.path.length - 1]! + 1}`; detail = `width ${cellAt(node.path)?.width ?? 1}`; }
      else { const b = blockAt(node.path)!; name = TYPES[b.type]?.[0] ?? b.type; detail = summary(b); }
      const li = el("li", { id, role: "treeitem", className: `tpl-dz-node tpl-dz-${node.kind}`, "aria-level": depth + 1, "aria-selected": key === selected, "data-node": key },
        el("span", { className: "tpl-dz-name", textContent: name }), el("span", { className: "tpl-dz-detail", textContent: detail }));
      li.style.setProperty("--depth", String(depth));
      li.addEventListener("click", () => { select(key); tree.focus(); });
      if (key === selected) tree.setAttribute("aria-activedescendant", id);
      return li;
    }));
    tree.querySelector<HTMLElement>("[aria-selected=true]")?.scrollIntoView({ block: "nearest" });
  };

  const select = (key: string) => {
    if (key === selected && !picking) return;
    selected = key;
    picking = false;
    lastKey = null;
    say("");
    renderTree();
    renderPanel(false);
    renderToolbar();
  };

  const renderAll = (keepFocus: boolean) => { renderTree(); renderPanel(keepFocus); renderToolbar(); };

  const focusPanel = () => panel.querySelector<HTMLElement>(FOCUSABLE)?.focus();

  // ---- settings form ----------------------------------------------------------------------------------------------
  /** Sets or clears a property (blank, false and NaN clear it, so the JSON keeps only what was chosen). */
  const put = <T extends object>(obj: T, prop: keyof T & string, value: unknown) => {
    const rec = obj as Record<string, unknown>;
    if (value === "" || value === false || value === undefined || (typeof value === "number" && Number.isNaN(value))) delete rec[prop];
    else rec[prop] = value;
  };

  const row = (label: string, control: HTMLElement) => el("label", { className: "tpl-dz-field" }, el("span", { textContent: label }), control);

  const text = (key: string, value: string | undefined, onInput: (v: string) => void, placeholder = "") => {
    const i = el("input", { value: value ?? "", placeholder, "data-key": key });
    i.addEventListener("input", () => { onInput(i.value); commit(key, false); });
    return i;
  };
  const num = (key: string, value: number | undefined, onInput: (v: number) => void, min: number, max: number, step = 0.5) => {
    const i = el("input", { type: "number", value: value === undefined ? "" : String(value), min: String(min), max: String(max), step: String(step), "data-key": key });
    i.addEventListener("input", () => { onInput(i.value === "" ? NaN : Number(i.value)); commit(key, false); });
    return i;
  };
  const choose = (key: string, value: string | undefined, options: [string, string][], onChange: (v: string) => void, structural = false) => {
    const s = el("select", { "data-key": key }, ...options.map(([v, l]) => el("option", { value: v, textContent: l, selected: v === (value ?? "") })));
    s.addEventListener("change", () => { onChange(s.value); commit(key, structural); });
    return s;
  };
  const tick = (key: string, value: boolean | undefined, onChange: (v: boolean) => void, structural = false) => {
    const c = el("input", { type: "checkbox", checked: !!value, "data-key": key });
    c.addEventListener("change", () => { onChange(c.checked); commit(key, structural); });
    return c;
  };
  const csv = (key: string, value: string[] | undefined, onInput: (v: string[] | undefined) => void, placeholder: string) =>
    text(key, (value ?? []).join(", "), (v) => { const parts = v.split(",").map((s) => s.trim()).filter(Boolean); onInput(parts.length ? parts : undefined); }, placeholder);
  const fieldOptions = (blank: string): [string, string][] => [["", blank], ...catalog.fields.map((f): [string, string] => [f.name, f.label])];

  /** A text box plus "Insert field" (Enter or the button puts {{field}} where the cursor was). */
  const textWithFields = (key: string, b: Block) => {
    const input = text(key, b.text, (v) => put(b, "text", v));
    const pick = el("select", { "data-key": `${key}:pick`, "aria-label": "Field to insert" }, ...fieldOptions("Insert a field…").map(([v, l]) => el("option", { value: v, textContent: l })));
    const insert = () => {
      if (!pick.value) return;
      const at = input.selectionStart ?? input.value.length;
      input.setRangeText(`{{${pick.value}}}`, at, input.selectionEnd ?? at, "end");
      pick.value = "";
      input.dispatchEvent(new Event("input"));
      input.focus();
    };
    pick.addEventListener("keydown", (e) => { if (e.key === "Enter" && pick.value) { e.preventDefault(); e.stopPropagation(); insert(); } });
    const go = button("Insert", "Put the field where the cursor is", insert);
    return el("div", { className: "tpl-dz-stack" }, row(b.type === "title" ? "Heading" : "Text", input), el("div", { className: "tpl-dz-inline" }, pick, go));
  };

  const style = (key: string, b: Block, opts: { align?: boolean; size?: boolean; bold?: boolean; italic?: boolean }) => [
    ...(opts.align ? [row("Align", choose(`${key}:align`, b.align, ALIGNS, (v) => put(b, "align", v)))] : []),
    ...(opts.size ? [row("Text size", num(`${key}:size`, b.size, (v) => put(b, "size", v), 5, 48))] : []),
    ...(opts.bold ? [row("Bold", tick(`${key}:bold`, b.bold, (v) => put(b, "bold", v)))] : []),
    ...(opts.italic ? [row("Italic", tick(`${key}:italic`, b.italic, (v) => put(b, "italic", v)))] : []),
  ];

  /** Fields ticked from the catalogue, in print order, each with its own settings. */
  const itemsEditor = (key: string, b: Block, from: Field[], mode: "fields" | "table") => {
    b.items ??= [];
    const items = b.items;
    const wrap = el("div", { className: "tpl-dz-items", role: "group", "aria-label": "Fields to show" });
    items.forEach((it, i) => {
      const k = `${key}:items.${i}`;
      const f = from.find((x) => x.name === it.field);
      const line = el("div", { className: "tpl-dz-item", "data-item": i },
        el("label", { className: "tpl-dz-tick" }, tick(`${k}:on`, true, () => items.splice(i, 1), true), el("span", { textContent: f?.label ?? it.field ?? "?" })),
        text(`${k}:label`, it.label, (v) => put(it, "label", v), f?.label ?? "Label"),
        ...(mode === "table"
          ? [num(`${k}:width`, it.width, (v) => put(it, "width", v), 0.5, 20), choose(`${k}:align`, it.align, ALIGNS, (v) => put(it, "align", v))]
          : [el("label", { className: "tpl-dz-tick" }, tick(`${k}:bold`, it.bold, (v) => put(it, "bold", v)), el("span", { textContent: "Bold" })),
             choose(`${k}:show_if`, it.show_if, fieldOptions("Always"), (v) => put(it, "show_if", v))]));
      line.querySelectorAll<HTMLElement>("input:not([type=checkbox])").forEach((x) => x.setAttribute("aria-label", mode === "table" && x.getAttribute("type") === "number" ? "Column width" : "Label"));
      line.querySelectorAll("select").forEach((x) => x.setAttribute("aria-label", mode === "table" ? "Align" : "Show only if"));
      wrap.append(line);
    });
    const rest = from.filter((f) => !items.some((it) => it.field === f.name));
    if (rest.length) {
      wrap.append(el("p", { className: "tpl-card-meta", textContent: "Tick to add:" }));
      rest.forEach((f, ri) => wrap.append(el("div", { className: "tpl-dz-item tpl-dz-off" },
        el("label", { className: "tpl-dz-tick" }, tick(`${key}:add.${f.name}`, false, () => {
          items.push({ field: f.name });
          // stay in the "tick to add" list, so several fields can be ticked one after another
          const after = rest[ri + 1] ?? rest[ri - 1];
          focusNext = after ? `${key}:add.${after.name}` : `${key}:items.${items.length - 1}:label`;
        }, true), el("span", { textContent: f.label })),
        el("span", { className: "tpl-card-meta", textContent: f.sample }))));
    }
    return el("fieldset", { className: "tpl-dz-set" }, el("legend", { textContent: mode === "table" ? "Columns (label, width, align)" : "Fields (label, bold, show only if)" }), wrap);
  };

  const pageForm = (): HTMLElement[] => {
    const p = (doc.page ??= {});
    const sizes = catalog.pageSizes.map((s): [string, string] => [s, PAGE_SIZES[s] ?? s]);
    return [
      el("h4", { textContent: "Page" }),
      row("Paper", choose("page:size", p.size ?? "a5_landscape", sizes, (v) => put(p, "size", v), true)),
      ...(p.size === "custom" ? [row("Width (mm)", num("page:width", p.width_mm, (v) => put(p, "width_mm", v), 50, 500, 1)),
        row("Height (mm)", num("page:height", p.height_mm, (v) => put(p, "height_mm", v), 50, 1000, 1))] : []),
      row("Margins (mm)", num("page:margin", p.margin_mm, (v) => put(p, "margin_mm", v), 0, 40)),
      row("Font size", num("page:font", p.font_size, (v) => put(p, "font_size", v), 5, 24)),
      row("Copy labels", csv("page:copies", p.copy_labels, (v) => put(p, "copy_labels", v), "e.g. Consignor Copy, Consignee Copy, Office Copy")),
      el("p", { className: "tpl-card-meta", textContent: "One page prints per copy; put a comma between the copy names." }),
    ];
  };

  const blockForm = (key: string, b: Block): HTMLElement[] => {
    const out: HTMLElement[] = [el("h4", { textContent: TYPES[b.type]?.[0] ?? b.type }), el("p", { className: "tpl-card-meta", textContent: TYPES[b.type]?.[1] ?? "" })];
    const lists = catalog.lists.map((l): [string, string] => [l.name, l.label]);
    switch (b.type) {
      case "company": out.push(...style(key, b, { size: true })); break;
      case "title": out.push(textWithFields(`${key}:text`, b), ...style(key, b, { align: true, size: true })); break;
      case "text": out.push(textWithFields(`${key}:text`, b), ...style(key, b, { align: true, size: true, bold: true, italic: true })); break;
      case "fields":
        out.push(row("Pairs per line", num(`${key}:columns`, b.columns, (v) => put(b, "columns", v), 1, 6, 1)), itemsEditor(key, b, catalog.fields, "fields"));
        break;
      case "amounts": out.push(itemsEditor(key, b, catalog.fields, "fields")); break;
      case "table":
        out.push(row("Rows from", choose(`${key}:rows`, b.rows, lists, (v) => { b.rows = v; b.items = (listOf(v)?.fields ?? []).map((f) => ({ field: f.name })); }, true)),
          itemsEditor(key, b, listOf(b.rows)?.fields ?? [], "table"));
        break;
      case "list": {
        const fields = (listOf(b.rows)?.fields ?? []).map((f): [string, string] => [f.name, f.label]);
        const setItem = (i: number, v: string) => {
          const items = (b.items ??= []);
          while (items.length <= i) items.push({});
          put(items[i]!, "field", v);
          if (items.every((x) => !x.field)) delete b.items;
        };
        out.push(row("Rows from", choose(`${key}:rows`, b.rows, lists, (v) => { b.rows = v; delete b.items; }, true)),
          row("Name", choose(`${key}:name`, b.items?.[0]?.field, [["", "Default (label)"], ...fields], (v) => setItem(0, v))),
          row("Amount", choose(`${key}:amount`, b.items?.[1]?.field, [["", "Default (amount)"], ...fields], (v) => setItem(1, v))));
        break;
      }
      case "badge":
        out.push(row("Field", choose(`${key}:field`, b.field, fieldOptions("Choose a field"), (v) => put(b, "field", v))), ...style(key, b, { align: true, size: true }));
        break;
      case "copy_label": out.push(...style(key, b, { align: true, size: true })); break;
      case "logo": out.push(...style(key, b, { align: true }), row("Height (mm)", num(`${key}:height`, b.height_mm, (v) => put(b, "height_mm", v), 4, 60))); break;
      case "spacer": out.push(row("Height (mm)", num(`${key}:height`, b.height_mm, (v) => put(b, "height_mm", v), 1, 100))); break;
      case "signatures": out.push(row("Names under the lines", csv(`${key}:labels`, b.labels, (v) => put(b, "labels", v), "e.g. Received by, For the company"))); break;
      case "row":
        out.push(el("p", { className: "tpl-card-meta", textContent: "Select a column in the list to add blocks into it or change its width." }),
          button("Add column", "Adds a column on the right", () => { (b.cells ??= []).push({ blocks: [] }); commit(null, true); }));
        break;
    }
    out.push(row("Show only if", choose(`${key}:show_if`, b.show_if, fieldOptions("Always"), (v) => put(b, "show_if", v))));
    if (b.type !== "row") out.push(el("p", { className: "tpl-card-meta", textContent: "Hidden when that field is empty, zero or No." }));
    return out;
  };

  const cellForm = (key: string, c: Cell): HTMLElement[] => [
    el("h4", { textContent: "Column" }),
    row("Width (share of the row)", num(`${key}:width`, c.width, (v) => put(c, "width", v), 0.5, 20)),
    el("p", { className: "tpl-card-meta", textContent: "Ctrl+Ins adds a block at the end of this column." }),
  ];

  const openPicker = () => {
    if (error) return;
    picking = true;
    const list = el("select", { size: 14, className: "tpl-dz-picker", "aria-label": "Block to add", "data-key": "picker" },
      ...catalog.blockTypes.map((t, i) => el("option", { value: t, selected: i === 0, textContent: `${TYPES[t]?.[0] ?? t} — ${TYPES[t]?.[1] ?? ""}` })));
    list.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); add(list.value); }
    });
    list.addEventListener("dblclick", () => add(list.value));
    panel.replaceChildren(el("h4", { textContent: "Add a block" }), list,
      el("div", { className: "tpl-dz-inline" }, button("Add", "Enter", () => add(list.value)), button("Cancel", "Esc", () => { picking = false; renderPanel(false); tree.focus(); })));
    list.focus();
  };

  const renderPanel = (keepFocus: boolean) => {
    const focusKey = focusNext ?? (keepFocus ? (document.activeElement as HTMLElement | null)?.dataset?.key : undefined);
    focusNext = null;
    if (error) { panel.replaceChildren(); return; }
    if (picking) return;
    const n = nodeOf(selected);
    const kids = n.kind === "page" ? pageForm()
      : n.kind === "cell" ? (cellAt(n.path) ? cellForm(selected, cellAt(n.path)!) : [])
      : (blockAt(n.path) ? blockForm(selected, blockAt(n.path)!) : []);
    panel.replaceChildren(...kids);
    if (focusKey) panel.querySelector<HTMLElement>(`[data-key="${CSS.escape(focusKey)}"]`)?.focus();
  };

  // ---- keyboard ---------------------------------------------------------------------------------------------------
  host.addEventListener("keydown", (e) => {
    if (ctrl(e) && !e.altKey && (e.key === "z" || e.key === "Z" || e.key === "y" || e.key === "Y")) {
      e.preventDefault();
      travel(e.key === "y" || e.key === "Y" || e.shiftKey ? 1 : -1);
      return;
    }
    if (ctrl(e) && e.key === "Insert") { e.preventDefault(); openPicker(); return; }
    if (e.key === "Escape") {
      e.stopPropagation(); // not the page's "discard changes?" prompt: Esc only leaves the settings here
      if (picking) { picking = false; renderPanel(false); }
      tree.focus();
      return;
    }
    if (e.target === tree || error) return;
    const t = e.target as HTMLElement;
    if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      const item = t.closest<HTMLElement>("[data-item]");
      const n = nodeOf(selected);
      const b = n.kind === "block" ? blockAt(n.path) : undefined;
      if (!item || !b?.items) return;
      e.preventDefault();
      const i = Number(item.dataset.item);
      const to = i + (e.key === "ArrowUp" ? -1 : 1);
      if (to < 0 || to >= b.items.length) return;
      [b.items[i], b.items[to]] = [b.items[to]!, b.items[i]!];
      const k = t.dataset.key?.replace(`items.${i}:`, `items.${to}:`);
      commit(null, true);
      if (k) panel.querySelector<HTMLElement>(`[data-key="${CSS.escape(k)}"]`)?.focus();
      return;
    }
    if (e.key === "Enter" && t.closest(".tpl-dz-panel") && (t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) {
      e.preventDefault();
      const all = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((x) => !(x as HTMLButtonElement).disabled);
      const next = all[all.indexOf(t) + (e.shiftKey ? -1 : 1)];
      if (next) next.focus(); else tree.focus();
    }
  });

  tree.addEventListener("keydown", (e) => {
    if (error) return;
    const list = nodes().map((x) => keyOf(x.node));
    const i = list.indexOf(selected);
    const go = (j: number) => { e.preventDefault(); const k = list[Math.max(0, Math.min(list.length - 1, j))]; if (k) select(k); };
    if (e.altKey && e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.altKey && e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(list.length - 1);
    else if (e.key === "Enter" || e.key === "F2") { e.preventDefault(); focusPanel(); }
    else if (e.key === "Insert") { e.preventDefault(); openPicker(); }
    else if (e.key === "Delete") { e.preventDefault(); remove(); }
  });

  // ---- start ------------------------------------------------------------------------------------------------------
  parse(source.value);
  renderAll(false);
  if (!document.activeElement || document.activeElement === document.body) tree.focus();
});
